/**
 * /forgot — identity-wide password reset request (ID.2: one credential
 * per identity, account-wide). The backend always returns a
 * success-shaped response — we never reveal whether the email exists.
 *
 * Email honesty (prospect drop-off review, 2026-10): when the server can't
 * send email it says so (`email_delivery: "unavailable"`, and the public
 * recovery-options read up front), and the page explains how to get back in
 * — the company's HR admin resets the password from People, an account
 * owner writes to support — instead of claiming a link is on its way.
 *
 * Migration note: 1:1 port of `frontend/src/app/(auth)/forgot/page.tsx`.
 * next/link → TanStack Router <Link>. No router/search-param coupling.
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { authApi } from "@/lib/api";
import { forgotSchema, type ForgotValues } from "@/lib/auth/schemas";
import { handleAuthError } from "@/lib/auth/form-errors";
import { forgotIntro, forgotOutcome } from "@/lib/password-recovery";
import type { ForgotPasswordResponse } from "@/types/auth";

const FIELDS = ["email"] as const;

export const Route = createFileRoute("/_auth/forgot")({
  component: ForgotPasswordPage,
});

function ForgotPasswordPage() {
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<ForgotPasswordResponse | null>(null);
  const [submittedEmail, setSubmittedEmail] = useState("");
  // Server configuration only (no account data): can a link be emailed?
  const recovery = useQuery({
    queryKey: ["auth", "recovery-options"],
    queryFn: ({ signal }) => authApi.getRecoveryOptions(signal),
    staleTime: 5 * 60_000,
    retry: false,
  });

  const form = useForm<ForgotValues>({
    resolver: zodResolver(forgotSchema),
    defaultValues: { email: "" },
  });

  async function onSubmit(values: ForgotValues) {
    setSubmitting(true);
    try {
      const res = await authApi.forgotPassword({ email: values.email });
      setSubmittedEmail(values.email);
      // dev_token is only returned outside production so the flow can
      // be completed without SMTP.
      setResult(res);
    } catch (err) {
      handleAuthError(err, form.setError, FIELDS);
    } finally {
      setSubmitting(false);
    }
  }

  if (result) {
    const devToken = result.dev_token ?? null;
    const outcome = forgotOutcome(result, submittedEmail);
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">{outcome.title}</CardTitle>
          <CardDescription>{outcome.body}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {outcome.steps.length > 0 && (
            <ol
              className="list-decimal space-y-2 pl-5 text-sm"
              data-testid="forgot-no-email-steps"
            >
              {outcome.steps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
          )}
          {outcome.steps.length > 0 && (
            <Button asChild variant="outline" className="w-full">
              <a href={`mailto:${outcome.supportEmail}?subject=${encodeURIComponent("I can't sign in to WorkRight")}`}>
                Email {outcome.supportEmail}
              </a>
            </Button>
          )}
          {devToken && (
            <div className="rounded-md border border-input bg-accent/40 p-3 text-xs">
              <p className="font-medium text-accent-foreground">
                Development mode
              </p>
              <p className="mt-1 break-all text-muted-foreground">
                Reset token: <code>{devToken}</code>
              </p>
            </div>
          )}
          {outcome.offerResetLink && (
            <Button asChild className="w-full">
              <Link
                to="/reset"
                search={{
                  email: submittedEmail,
                  ...(devToken ? { token: devToken } : {}),
                }}
              >
                Continue to Reset Password
              </Link>
            </Button>
          )}
          <p className="text-center text-sm text-muted-foreground">
            <Link
              to="/login"
              className="inline-flex items-center font-medium text-primary hover:underline [@media(pointer:coarse)]:min-h-11"
            >
              Back to Sign In
            </Link>
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-2xl">Reset Your Password</CardTitle>
        <CardDescription>{forgotIntro(recovery.data)}</CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4"
            noValidate
          >
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      autoComplete="email"
                      placeholder="you@company.com"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button
              type="submit"
              className="w-full"
              disabled={submitting}
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {recovery.data && !recovery.data.email_reset_available
                ? "Show Me How to Get Back In"
                : "Send Reset Link"}
            </Button>
          </form>
        </Form>
        <p className="mt-6 text-center text-sm text-muted-foreground">
          <Link
            to="/login"
            className="inline-flex items-center font-medium text-primary hover:underline [@media(pointer:coarse)]:min-h-11"
          >
            Back to Sign In
          </Link>
        </p>
      </CardContent>
    </Card>
  );
}
